import React, { useEffect, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import { useVideo } from "../../../context/VideoContext";
import { useWordTranslation } from "../../../hooks/useWordTranslation";
import TranslationSection from "./TranslationSection";
import DefinitionSection from "./DefinitionSection";
import ExternalDictBar from "./ExternalDictBar";
import SaveWordButton from "./SaveWordButton";
import { useNotification } from "../../../context/NotificationContext";
import { usePackagesModal } from "../../../context/PackagesModalContext";
import "./WordInfoModal.css";

const formatDictToText = (dict) => {
  if (!dict || dict.length === 0) return "";
  return dict
    .map((item) => {
      const pos = item.pos || "nopos";
      const translations = (item.translations || []).join("، ");
      return `${pos} : ${translations}`;
    })
    .join("\n");
};

const WordInfoModal = ({
  isOpen,
  word = "",
  fullText = "",
  onClose,
  fromLang = "en",
  toLang = "fa",
  movieName = "",
  season = "",
  episode = "",
  subtitle1Text = "",
  subtitle2Text = "",
  isSentenceMode = false,
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const { showPackages } = usePackagesModal();
  const [wasVideoFullscreenBeforePopup, setWasVideoFullscreenBeforePopup] =
    useState(false);
  const [translationData, setTranslationData] = useState(null);
  const [loadingState, setLoadingState] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [editableWord, setEditableWord] = useState(word);
  const [editableMeanings, setEditableMeanings] = useState("");
  const [isModalFullscreen, setIsModalFullscreen] = useState(false);
  const [editText, setEditText] = useState("");
  const [isDictBarVisible, setIsDictBarVisible] = useState(true);

  const {
    setShortcutsEnabled,
    toggleFullscreen: toggleVideoFullscreen,
    isFullscreen: isVideoFullscreen,
    isIOS,
    containerRef,
    setIsModalOpen,
  } = useVideo();

  const { getTranslation, isPremium, remainingQuota } = useWordTranslation();
  const { showError } = useNotification();

  const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;

  useEffect(() => {
    setEditableWord(word);
  }, [word]);

  useEffect(() => {
    if (isOpen && word) {
      fetchTranslation();
    }
  }, [isOpen, word, fromLang]);

  useEffect(() => {
    if (isOpen) {
      setIsModalOpen(true);
      document.body.style.overflow = "hidden";
      setShortcutsEnabled(false);
      setIsDictBarVisible(true);
    }

    return () => {
      setIsModalOpen(false);
      document.body.style.overflow = "";
      setShortcutsEnabled(true);

      if (wasVideoFullscreenBeforePopup && !isVideoFullscreen) {
        toggleVideoFullscreen();
      }
    };
  }, [
    isOpen,
    setShortcutsEnabled,
    wasVideoFullscreenBeforePopup,
    isVideoFullscreen,
    toggleVideoFullscreen,
    setIsModalOpen,
  ]);

  useEffect(() => {
    if (translationData?.dict) {
      const formatted = formatDictToText(translationData.dict);
      setEditableMeanings(formatted);
      setEditText(formatted);
    }
  }, [translationData]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen && !showPackages) {
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, showPackages]);

  const fetchTranslation = async () => {
    setLoadingState("loading");
    setErrorMessage("");

    try {
      const result = await getTranslation(fromLang, toLang, word);

      if (result?.data) {
        setTranslationData(result.data);
        setLoadingState("success");
      } else if (result?.error === "QUOTA_EXCEEDED") {
        setLoadingState("quota_exceeded");
        setErrorMessage(result.message);
      } else if (result?.error) {
        setLoadingState("error");
        setErrorMessage(result.message || "خطا در دریافت ترجمه");
        showError(result.message || "خطا در دریافت ترجمه", {
          duration: 4000,
          position: "top-center",
        });
      }
    } catch (error) {
      console.error("Translation fetch failed:", error);
      setLoadingState("error");
      setErrorMessage("خطا در ارتباط با سرور. لطفاً دوباره تلاش کنید.");
    }
  };

  const handleClose = () => {
    setIsEditMode(false);
    setWasVideoFullscreenBeforePopup(false);
    onClose();
  };

  const handleExternalDictClick = () => {
    setWasVideoFullscreenBeforePopup(isVideoFullscreen);
    if (isVideoFullscreen) {
      toggleVideoFullscreen();
    }
    setIsEditMode(true);
  };

  const toggleModalFullscreen = () => {
    setIsModalFullscreen((prev) => !prev);
  };

  const handleRequirePremium = (message) => {
    showPackages({
      onSelect: (pkg) => {
      },
    });
    showError(message, {
      duration: 4000,
      position: "top-center",
    });
  };

  const handleSaveComplete = () => {
    handleClose();
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setEditText((prev) => {
          const trimmed = prev.trim();
          return trimmed ? trimmed + "\n--------\n" + text : text;
        });
      }
    } catch {
      showError("خطا در خواندن کلیپ‌بورد", {
        duration: 3000,
        position: "top-center",
      });
    }
  };

  const getPortalContainer = useCallback(() => {
    if (isIOS && isVideoFullscreen && containerRef?.current) {
      return containerRef.current;
    }

    if (!isIOS && isVideoFullscreen) {
      const fullscreenElement =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;

      if (fullscreenElement) {
        return fullscreenElement;
      }
    }

    return document.body;
  }, [isIOS, isVideoFullscreen, containerRef]);

  if (!isOpen || !word) return null;

  const portalContainer = getPortalContainer();

  const modalContent = (
    <div
      className={`word-info-modal-overlay`}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        handleClose();
      }}
      onTouchStart={(e) => e.stopPropagation()}
      onTouchEnd={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div
        className={`word-info-modal ${isModalFullscreen ? "modal-fullscreen" : ""}`}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div className="header-left">
            <h3>اطلاعات لغت</h3>
            {!isPremium && (
              <div className="quota-badge">
                {remainingQuota(fromLang)}/{10} رایگان
              </div>
            )}
          </div>
          <div className="header-actions">
            <button
              className="modal-btn-wordinfo icon-btn toggle-dict-btn"
              onClick={() => setIsDictBarVisible(!isDictBarVisible)}
              title={
                isDictBarVisible ? "مخفی کردن دیکشنری‌ها" : "نمایش دیکشنری‌ها"
              }
            >
              {isDictBarVisible ? "📚" : "📖"}
            </button>
            <button
              className="modal-btn-wordinfo icon-btn fullscreen-btn"
              onClick={toggleModalFullscreen}
              title={isModalFullscreen ? "حالت عادی" : "تمام صفحه"}
            >
              {isModalFullscreen ? "🗗" : "🗖"}
            </button>
            <button
              className="modal-btn-wordinfo icon-btn close-btn-wordinfo"
              onClick={handleClose}
              title="بستن"
            >
              ✕
            </button>
          </div>
        </div>

        {isDictBarVisible && (
          <ExternalDictBar
            fromLang={fromLang}
            toLang={toLang}
            word={word}
            onOpenPopup={handleExternalDictClick}
          />
        )}

        {remainingQuota(fromLang) == 0 ? (
          <div className="modal-content-wordinfo">
            <div className="sentence-translation">
              محدودیت در استفاده روزانه، لطفا اشتراک تهیه کنید
            </div>
            <div
              className={`word-display`}
              style={{ display: "flex", justifyContent: "center" }}
            >
              <button
                className="modal-btn-wordinfo edit-btn"
                onClick={() =>
                  handleRequirePremium("سهمیه رایگان شما تمام شده است")
                }
              >
                دریافت اشتراک
              </button>
            </div>
          </div>
        ) : (
          <div className="modal-content-wordinfo">
            <div className="word-section">
              <div className="section-label">
                {isSentenceMode ? "جمله:" : "کلمه:"}
              </div>
              {isEditMode ? (
                <input
                  type="text"
                  value={editableWord}
                  onChange={(e) => setEditableWord(e.target.value)}
                  className="edit-input"
                />
              ) : (
                <div
                  className={`word-display ${
                    isSentenceMode ? "sentence-mode" : ""
                  }`}
                >
                  {isSentenceMode ? fullText : word}
                </div>
              )}
            </div>

            <div className="translation-main-section">
              <div className="section-label">
                {isSentenceMode ? "ترجمه:" : "ترجمه‌ها:"}
              </div>
              {isSentenceMode ? (
                <div className="sentence-translation">
                  {subtitle2Text || "ترجمه‌ای موجود نیست"}
                </div>
              ) : (
                <>
                  {loadingState === "success" && translationData && (
                    isEditMode ? (
                      <div className="edit-translation-area">
                        <textarea
                          className="edit-textarea"
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          dir="rtl"
                          placeholder="particle : ترجمه1، ترجمه2"
                        />
                        <button
                          className="paste-btn"
                          onClick={handlePaste}
                          title="چسباندن از کلیپ‌بورد"
                          type="button"
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
                            <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
                            <rect x="10" y="9" width="4" height="4" rx="1"/>
                            <path d="M10 13h4v4h-4z"/>
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <TranslationSection data={translationData} />
                    )
                  )}
                </>
              )}
            </div>

            {loadingState === "success" && translationData?.enData && (
              <DefinitionSection enData={translationData.enData} />
            )}
          </div>
        )}

        <div className="modal-footer">
          {remainingQuota(fromLang) == 0 ? null : (
            <div className="footer-left">
              <button
                className="modal-btn-wordinfo edit-btn"
                onClick={() => {
                  if (isEditMode) {
                    setEditText(editableMeanings);
                  }
                  setIsEditMode(!isEditMode);
                }}
              >
                {isEditMode ? "انصراف از ویرایش" : "ویرایش و ذخیره"}
              </button>

              <SaveWordButton
                word={isEditMode ? editableWord : word}
                fromLang={fromLang}
                toLang={toLang}
                meanings={isEditMode ? editText : editableMeanings}
                subtitle={subtitle1Text}
                translateOfSubtitle={subtitle2Text}
                movieName={movieName}
                season={season}
                episode={episode}
                isPremium={isPremium}
                onRequirePremium={handleRequirePremium}
                onSaveComplete={handleSaveComplete}
              />
            </div>
          )}

          <button
            className="modal-btn-wordinfo close-btn-wordinfo"
            onClick={handleClose}
          >
            بستن
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, portalContainer);
};

export default WordInfoModal;
